import { useCallback } from 'react';

import { type AirActionTrackingLocation, useTrackDownloadedItems } from '@air/analytics';
import { Clips } from '@air/api';
import { type AltResolution, type Clip } from '@air/api/types';
import { useToasts } from '@air/provider-toast';
import { reportErrorToBugsnag } from '@air/utils-error';

import { usePrivateAssetOpenLocation } from '~/hooks/usePrivateAssetOpenLocation';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { DownloadFile, isS3DownloadUrlActive } from '~/utils/DownloadFile';
import { getPrivateDownloadUrlOptions } from '~/utils/internalAnalytics/download';

export type DownloadPrivateAssetParams = {
  altResolutionId?: AltResolution['id'];
  clipId: Clip['id'];
  trackLocation: AirActionTrackingLocation;
};

/**
 * This hook returns a function that downloads a single private asset.
 * If you are trying to download multiple private assets, @see useDownloadPrivateItems
 */
export const useDownloadPrivateAsset = () => {
  const { showToast } = useToasts();
  const { trackDownloadedItems } = useTrackDownloadedItems();
  const { currentWorkspace } = useCurrentWorkspace();
  const workspaceId = currentWorkspace?.id;
  const analyticsLocation = usePrivateAssetOpenLocation();

  const downloadPrivateAsset = useCallback(
    async ({ altResolutionId, clipId, trackLocation }: DownloadPrivateAssetParams) => {
      if (!workspaceId) {
        throw new Error('No workspace id');
      }

      try {
        const { url } = await Clips.getDownloadUrl({
          clipId,
          workspaceId,
          options: getPrivateDownloadUrlOptions({ altResolutionId, analyticsLocation }),
        });

        if (isS3DownloadUrlActive(url)) {
          DownloadFile(url);

          trackDownloadedItems({
            clipIds: [clipId],
            altResolutionIds: altResolutionId ? [altResolutionId] : undefined,
            location: trackLocation,
          });
        } else {
          showToast('One or more of the selected file downloads has expired.');
        }
      } catch (error) {
        showToast('There was an error downloading a private asset.');

        reportErrorToBugsnag({
          error,
          context: 'Failed to download private assets from selection menu',
          metadata: {
            data: {
              clipId,
              altResolutionId,
              location: trackLocation,
              analyticsLocation,
            },
          },
        });
      }
    },
    [analyticsLocation, showToast, trackDownloadedItems, workspaceId],
  );

  return {
    downloadPrivateAsset,
  };
};
